import { useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { OnboardingData } from "@/pages/onboarding/Onboarding";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { ArrowRight } from "lucide-react";

interface Props {
  data: OnboardingData;
  updateData: (fields: Partial<OnboardingData>) => void;
  nextStep: () => void;
}

// Helpers

/** Strip everything except letters, numbers, spaces, hyphens, ampersands, apostrophes */
const sanitizeBusinessName = (v: string) =>
  v.replace(/[^a-zA-Z0-9\s&''-]/g, "").slice(0, 120);

/** Standard email validation regex */
const isValidEmail = (email: string) =>
  /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email);

// Component

const BusinessIdentityStep = ({ data, updateData, nextStep }: Props) => {
  const [errors, setErrors] = useState<Record<string, string>>({});

  // onChange handlers

  const handleBusinessName = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const sanitized = sanitizeBusinessName(e.target.value);
      updateData({ businessName: sanitized });
    },
    [updateData]
  );

  const handleBusinessEmail = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const email = e.target.value;
      updateData({ businessEmail: email });

      if (!email.trim()) {
        setErrors((prev) => {
          const next = { ...prev };
          delete next.businessEmail;
          return next;
        });
        return;
      }

      if (isValidEmail(email)) {
        setErrors((prev) => {
          const next = { ...prev };
          delete next.businessEmail;
          return next;
        });
      } else {
        setErrors((prev) => ({
          ...prev,
          businessEmail: "Please enter a valid email address",
        }));
      }
    },
    [updateData]
  );

  const handleBusinessAddress = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      updateData({ businessAddress: e.target.value.slice(0, 120) });
    },
    [updateData]
  );

  const handlePhone = useCallback(
    (value: string | undefined) => {
      const phone = value || "";
      updateData({ phone });

      // Clear error when empty (optional field)
      if (!phone) {
        setErrors((prev) => {
          const { phone: _, ...rest } = prev;
          return rest;
        });
        return;
      }

      // Validate using libphonenumber
      if (isValidPhoneNumber(phone)) {
        setErrors((prev) => {
          const { phone: _, ...rest } = prev;
          return rest;
        });
      } else if (phone.length > 5) {
        setErrors((prev) => ({
          ...prev,
          phone: "Enter a valid phone number",
        }));
      }
    },
    [updateData]
  );

  // Validation gate

  const hasErrors = Object.keys(errors).length > 0;
  const canContinue =
    !!data.businessName && !!data.businessEmail && !!data.businessAddress && !hasErrors;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canContinue) return;
    nextStep();
  };

  // Inline error component
  const FieldError = ({ field }: { field: string }) =>
    errors[field] ? (
      <p className="text-[11px] text-destructive mt-1">{errors[field]}</p>
    ) : null;

  return (
    <form onSubmit={handleSubmit} className="flex min-h-full flex-1 flex-col">
      <div className="space-y-4">
        {/* Business name */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between gap-4">
            <Label htmlFor="businessName">Business name</Label>
            <span className="text-[10px] tabular-nums text-muted-foreground">{data.businessName.length}/120</span>
          </div>
          <Input
            id="businessName"
            placeholder="e.g. Acme Studio"
            value={data.businessName}
            onChange={handleBusinessName}
            maxLength={120}
            required
          />
        </div>

        {/* Business Email */}
        <div className="space-y-1.5">
          <Label htmlFor="businessEmail" className={cn(errors.businessEmail && "text-destructive")}>Business email</Label>
          <Input
            id="businessEmail"
            type="email"
            placeholder="hello@acmestudio.com"
            value={data.businessEmail}
            onChange={handleBusinessEmail}
            required
            aria-invalid={!!errors.businessEmail}
            className={cn(errors.businessEmail && "border-destructive focus-visible:ring-destructive")}
          />
          <FieldError field="businessEmail" />
        </div>

        {/* Business address */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between gap-4">
            <Label htmlFor="businessAddress">Business address</Label>
            <span className="text-[10px] tabular-nums text-muted-foreground">{data.businessAddress.length}/120</span>
          </div>
          <Input
            id="businessAddress"
            placeholder="123 Main St, Suite 100"
            value={data.businessAddress}
            onChange={handleBusinessAddress}
            maxLength={120}
            required
          />
        </div>

        {/* Phone with country dropdown */}
        <div className="space-y-1.5">
          <Label htmlFor="businessPhone" className={cn(errors.phone && "text-destructive")}>Phone number (optional)</Label>
          <div className={cn(
            "flex h-10 w-full items-center rounded-md border border-input bg-card px-3 ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2",
            errors.phone && "border-destructive focus-within:ring-destructive"
          )}>
            <PhoneInput
              id="businessPhone"
              international
              defaultCountry="SG"
              placeholder="8123 4567"
              value={data.phone || ""}
              onChange={handlePhone}
              className="phone-input-onboarding w-full"
            />
          </div>
          <FieldError field="phone" />
        </div>

      </div>

      <div className="mt-auto flex justify-end">
        <Button type="submit" size="sm" disabled={!canContinue}>
          Continue
          <ArrowRight className="ml-1.5 h-4 w-4" />
        </Button>
      </div>
    </form>
  );
};

export default BusinessIdentityStep;
